/* eslint-disable @typescript-eslint/explicit-member-accessibility, @typescript-eslint/explicit-function-return-type */
import { startCase } from 'lodash'
import React from 'react'
import intl from 'react-intl-universal'
import { connect } from 'react-redux'
import { compose, withHandlers, withState, withStateHandlers } from 'recompose'
import { Dispatch } from 'redux'
import {
  applyFeaturedMedia,
  getFeaturedMedia,
  getCustomEmbed,
} from '~/redux/settingsReducer.js'
import { IFeaturedMediaForm } from '../Modal/types'
import {
  IFeaturedMediaSettings,
  IFeaturedMediaSettingsItem,
  IFeaturedMediaSettingsOption,
  IFeaturedMediaSettingsOptions,
} from '../types'
import FeaturedMediaFormContainerSFC from './featuredMediaFormContainer.sfc'
import {
  IFeaturedMediaCardsState,
  IFeaturedMediaFormContainerSFC,
  IReduxConnected,
  TLifecycleWrapperProps,
} from './types'
import { IEllipsisReduxStore } from '~/redux/types'
import { getBuiltinTypes } from '../builtinTypes'
import { getKey } from '~/util/customEmbed'
import { getEvidentlyFeaturesSync } from '~/evidentlyFeatureFlags'

const _migrateFeaturedMedia = (featuredMedia: {}): IFeaturedMediaSettings => {
  const keys = Object.keys(featuredMedia)
  return keys.reduce(
    (result: IFeaturedMediaSettings, key: string): IFeaturedMediaSettings => {
      const item: IFeaturedMediaSettingsItem = featuredMedia[key]
      return {
        ...result,
        [key]: {
          // @ts-expect-error
          label: startCase(key),
          // @ts-expect-error
          requiredToPublish: false,
          ...item,
        },
      }
    },
    {}
  )
}

export const _removeUnavailableCustomEmbeds = (
  featuredMedia: IFeaturedMediaSettings,
  customEmbed: IEllipsisReduxStore['settings']['customEmbed']
): IFeaturedMediaSettingsOptions => {
  const availableTypes = {
    ...Object.entries(customEmbed).reduce((obj, [subtype, item]) => {
      obj[getKey(subtype)] = item.caption || getKey(subtype)
      return obj
    }, {}),
    ...getBuiltinTypes(getEvidentlyFeaturesSync()).reduce((obj, type) => {
      obj[type] = intl.get(`settings.featuredMedia.${type}`)
      return obj
    }, {}),
  }
  const typeKeys = Object.keys(availableTypes)
  return Object.keys(featuredMedia).reduce(
    (result: IFeaturedMediaSettingsOptions, key: string) => {
      const remainingTypes = featuredMedia[key].types
        .filter((type) => {
          return typeKeys.includes(type)
        })
        .map((key) => ({
          key,
          label: availableTypes[key],
        }))
      if (remainingTypes.length === 0) {
        return result
      }
      return {
        ...result,
        [key]: {
          ...featuredMedia[key],
          types: remainingTypes,
        },
      }
    },
    {}
  )
}

export const _mapToProps = (state: {}) => {
  const featuredMedia: IFeaturedMediaSettings =
    _migrateFeaturedMedia(getFeaturedMedia(state)) || {}
  return {
    featuredMedia: _removeUnavailableCustomEmbeds(
      featuredMedia,
      getCustomEmbed(state)
    ),
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const _dispatchToProps = (dispatch: Dispatch<any>) => {
  return {
    applyFeaturedMedia: (payload) => dispatch(applyFeaturedMedia(payload)),
  }
}

export const _mapFeaturedMediaKeys = (props: IReduxConnected) => {
  return {
    cards: Object.keys(props.featuredMedia).map((key) => {
      return {
        key,
        // @ts-expect-error
        value: { requiredToPublish: false, ...props.featuredMedia[key] },
      }
    }),
  }
}

export const ERROR_HAVE_BASIC = intl.get('settings.featuredMedia.basicError')
export const _checkAddErrorMessage = (
  state: IFeaturedMediaCardsState
): IFeaturedMediaCardsState => {
  const itemWithBasic = state.cards.find((card) => card.key === 'basic')
  return {
    ...state,
    errorMessage: itemWithBasic ? undefined : ERROR_HAVE_BASIC,
  }
}

export const _stateHandlers = {
  doAdd: ({ cards }: IFeaturedMediaCardsState) => (
    key: string,
    value: IFeaturedMediaSettingsOption
  ) => {
    return _checkAddErrorMessage({
      cards: [{ key, value }, ...cards],
    })
  },
  doRemove: ({ cards }: IFeaturedMediaCardsState) => (key: string) => {
    return _checkAddErrorMessage({
      cards: cards.filter((card) => card.key !== key),
    })
  },
  doToggleRequired: ({ cards }: IFeaturedMediaCardsState) => (key: string) => {
    return _checkAddErrorMessage({
      cards: cards.map((card) => {
        if (card.key === key) {
          card.value.requiredToPublish = !card.value.requiredToPublish
        }
        return card
      }),
    })
  },
}

export const _handlers = {
  canAddKey: (props: IFeaturedMediaCardsState) => (key: string): boolean => {
    return props.cards.find((card) => card.key === key) === undefined
  },
  validateAndApply: (
    props: IFeaturedMediaCardsState & IReduxConnected
  ) => () => {
    if (props.errorMessage) {
      return false
    }

    const featuredMedia: IFeaturedMediaSettings = props.cards.reduce(
      (result, card) => {
        return {
          ...result,
          [card.key]: {
            ...card.value,
            types: card.value.types.map(({ key }) => key),
          },
        }
      },
      {}
    )

    props.applyFeaturedMedia(featuredMedia)
    return true
  },
}

export const _wrapperLifecycle = (
  Component: React.ComponentClass<TLifecycleWrapperProps, {}>
) =>
  // eslint-disable-next-line react/display-name
  class extends React.Component<TLifecycleWrapperProps, {}> {
    componentDidMount() {
      this.props.validator.setCurrent(this.props)
    }

    componentWillUnmount() {
      this.props.validator.setCurrent(null)
    }

    render() {
      return React.createElement(Component, this.props)
    }
  }

const enhancer = compose<IFeaturedMediaFormContainerSFC, IFeaturedMediaForm>(
  connect(_mapToProps, _dispatchToProps),
  withState('errorMessage', 'setErrorMessage', ''),
  withStateHandlers(_mapFeaturedMediaKeys, _stateHandlers),
  withHandlers(_handlers),
  _wrapperLifecycle
)

export const FeaturedMediaContainer = enhancer(FeaturedMediaFormContainerSFC)
